import { useState, useEffect } from 'react';
import httpClient from 'API/httpClient';
import { ProductItem, SearchFilter, CategoryFacet } from 'types/item';
import { GroupIdResponse } from 'types/group';
import { ApiError } from 'types/apiError';

const useProductData = (
  categoryName?: string | null,
  partNumber?: string | null,
  filters?: SearchFilter[] | null,
  searchInput?: string | null,
  itemcode?: string | null,
) => {
  const [data, setData] = useState<ProductItem[]>([]);
  const [total, setTotal] = useState(0);
  const [categoryFacets, setCategoryFacets] = useState<CategoryFacet[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const query =
      searchInput == null || searchInput === ''
        ? ''
        : String(searchInput).trim();

    // Globalna podpowiedź (bez kategorii): puste pole = brak danych i API
    const globalTypeaheadOnly =
      !categoryName &&
      (!filters || filters.length === 0) &&
      !itemcode &&
      !partNumber;

    if (globalTypeaheadOnly && !query) {
      setData([]);
      setTotal(0);
      setCategoryFacets([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    setLoading(true);
    setError(null);

    const getProductsApi = async () => {
      let searchFilters: SearchFilter[] = [];

      let categoryCode: number | null = null;
      if (categoryName) {
        try {
          const groupResponse = await httpClient.get<GroupIdResponse>(
            `/group/${categoryName}/id`,
          );
          categoryCode = groupResponse.data.groupId;
        } catch {
          // Best-effort lookup: search proceeds without a category filter.
        }
      }

      if (filters) {
        searchFilters = [...filters];
      }

      if (searchInput) {
        searchFilters.push({
          columnName: 'Search',
          value: searchInput,
          operator: 'contains',
        });
      }

      if (itemcode) {
        searchFilters.push({
          columnName: 'ItemCode',
          value: itemcode,
          operator: 'eq',
        });
      }

      if (categoryCode && categoryCode !== -1) {
        searchFilters.push({
          columnName: 'Group',
          value: categoryCode,
          operator: 'eq',
        });
      }

      const response = await httpClient.post('/search', searchFilters);
      return response.data;
    };

    const timeoutId = setTimeout(async () => {
      try {
        const {
          items,
          total: resultTotal,
          categoryFacets: resultCategoryFacets,
        } = await getProductsApi();
        if (!cancelled) {
          setData(items);
          setTotal(resultTotal);
          setCategoryFacets(resultCategoryFacets ?? []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err as ApiError);
          setData([]);
          setTotal(0);
          setCategoryFacets([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      setLoading(false);
    };
  }, [categoryName, partNumber, filters, searchInput, itemcode]);

  return { data, total, categoryFacets, loading, error };
};

export default useProductData;
